/**
 * FTP admin API klient pro SPA.
 */

const BASE = "/api/admin/ftp";

export interface FtpStatus {
  config: {
    host: string;
    user: string | null;
    passwordSet: boolean;
    secure: boolean;
    mirrorDir: string;
  };
  lastSync: FtpSyncRun | null;
  pendingFilesCount: number;
  inventoryByStatus: Array<{ status: string; count: number }>;
}

export interface FtpSyncRun {
  runId: string;
  startedAt: string;
  finishedAt: string | null;
  durationSec?: number | null;
  status: string;
  trigger: string;
  filesDiscovered: number;
  filesNew: number;
  filesModified: number;
  filesDeleted?: number;
  pricesImported: number;
  replacementsImported: number;
  errors: number;
  errorMessage?: string | null;
}

export interface FtpFile {
  id: number;
  path: string;
  filename: string;
  directory: string;
  sizeBytes: number;
  modifiedAt: string;
  classification: "price_list" | "replacement" | "pim_xml" | "unknown";
  salesOrg: string | null;
  brandCode: string | null;
  status: "pending" | "processed" | "ignored" | "monitored" | "error";
  firstSeenAt: string;
  lastSeenAt: string;
  lastChangedAt: string;
  lastProcessedAt: string | null;
  notes: string | null;
}

export interface FtpChange {
  id: number;
  path: string;
  changeType: "new" | "modified" | "deleted";
  oldSizeBytes: number | null;
  newSizeBytes: number | null;
  oldModifiedAt: string | null;
  newModifiedAt: string | null;
  detectedAt: string;
  runId: string;
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
    ...init,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => "")}`);
  }
  return res.json() as Promise<T>;
}

export async function getFtpStatus(): Promise<FtpStatus> {
  return json<FtpStatus>(`/status`);
}

export async function getFtpHistory(limit = 50): Promise<{ items: FtpSyncRun[] }> {
  return json(`/history?limit=${limit}`);
}

export async function getFtpFiles(params: {
  status?: string;
  classification?: string;
} = {}): Promise<{ items: FtpFile[] }> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.classification) qs.set("classification", params.classification);
  return json(`/files?${qs}`);
}

export async function getFtpChanges(limit = 100): Promise<{ items: FtpChange[] }> {
  return json(`/changes?limit=${limit}`);
}

export async function triggerFtpSync(dryRun = false): Promise<{ accepted: boolean; message: string }> {
  return json(`/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ skipDownload: dryRun }),
  });
}

export async function patchFtpFile(
  id: number,
  status: FtpFile["status"],
  notes?: string,
): Promise<{ id: number; status: string }> {
  return json(`/files/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ status, notes }),
  });
}
